import { useQuery } from "@tanstack/react-query";
import api, { fmtTL, CATEGORIES, EXPENSE_CATEGORIES } from "@/lib/api";
import { Wallet, Users, Receipt, GitBranch, TrendingDown, AlertTriangle, Clock, ArrowUpRight } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const StatCard = ({ icon: Icon, label, value, sub, color, delay, testid }) => (
  <div
    data-testid={testid}
    className="glass rounded-2xl p-5 fade-up"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center justify-between">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20`, color }}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="mt-4 text-2xl font-heading font-bold text-white tabular">{value}</div>
    <div className="text-xs text-slate-400 mt-1">{label}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
  });

  if (isLoading || !data)
    return <div className="text-slate-400 text-sm py-20 text-center">Yükleniyor…</div>;

  const pieData = [
    ...data.category_breakdown.map((c) => ({
      name: CATEGORIES[c.category]?.label || c.category,
      value: c.amount,
      color: CATEGORIES[c.category]?.color || "#94A3B8",
    })),
    ...(data.expense_breakdown || []).map((c) => ({
      name: EXPENSE_CATEGORIES[c.category]?.label || c.category,
      value: c.amount,
      color: EXPENSE_CATEGORIES[c.category]?.color || "#94A3B8",
    })),
  ];

  const branchBar = data.branch_breakdown.map((b) => ({
    name: b.name.replace(" Şubesi", "").replace(" Şube", ""),
    Maaş: Math.round(b.salary),
    Faturalar: Math.round(b.bills),
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Gösterge Paneli</h1>
        <p className="text-slate-400 text-sm mt-1">Tüm giderleriniz tek pencerede</p>
      </div>

      {/* Big all-expenses window */}
      <div className="glass rounded-2xl p-6 sm:p-8 fade-up relative overflow-hidden" data-testid="all-expenses-window">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 blur-3xl rounded-full -mr-20 -mt-20" />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Toplam Aylık Gider
            </div>
            <div className="mt-2 text-4xl sm:text-5xl font-heading font-extrabold text-white tabular" data-testid="total-expense-value">
              {fmtTL(data.total_expense)}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <span className="text-slate-300">Maaşlar: <b className="text-white tabular">{fmtTL(data.salary_total)}</b></span>
              <span className="text-slate-300">Faturalar: <b className="text-white tabular">{fmtTL(data.bills_total)}</b></span>
              <span className="text-slate-300">Masraflar: <b className="text-white tabular">{fmtTL(data.expense_total)}</b></span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span data-testid="merkez-total" className="px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">Merkez: <b className="tabular">{fmtTL(data.merkez_total)}</b></span>
              <span data-testid="sube-total" className="px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-400">Şubeler: <b className="tabular">{fmtTL(data.sube_total)}</b></span>
              {data.usd_rate && (
                <span data-testid="usd-rate-chip" className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                  USD Alış (Harem): <b className="tabular">₺{data.usd_rate.rate.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</b>
                  {data.usd_staff_count > 0 && <> · {data.usd_staff_count} USD personel: <b className="tabular">${data.usd_salary_total.toLocaleString("tr-TR")}</b></>}
                  {data.usd_rate.source !== "harem" && <> · son kayıtlı kur</>}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-rose-400">Ödenmemiş</div>
              <div className="text-lg font-bold text-white tabular">{fmtTL(data.unpaid_total)}</div>
            </div>
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-emerald-400">Ödenen</div>
              <div className="text-lg font-bold text-white tabular">{fmtTL(data.paid_total)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-salary" icon={Wallet} label="Toplam Maaş Gideri" value={fmtTL(data.salary_total)} sub={`Asgari: ${fmtTL(data.base_total)} + Ek: ${fmtTL(data.extra_total)}${data.prim_total > 0 ? ` + Prim: ${fmtTL(data.prim_total)}` : ""}${data.absence_deduction_total > 0 ? ` − Devamsızlık: ${fmtTL(data.absence_deduction_total)}` : ""}${data.avans_total > 0 ? ` − Avans: ${fmtTL(data.avans_total)}` : ""}`} color="#3B82F6" delay={0} />
        <StatCard testid="stat-bills" icon={Receipt} label="Toplam Fatura" value={fmtTL(data.bills_total)} sub={`${data.category_breakdown.length} kategori`} color="#F59E0B" delay={80} />
        <StatCard testid="stat-staff" icon={Users} label="Personel Sayısı" value={data.staff_count} color="#10B981" delay={160} />
        <StatCard testid="stat-branch" icon={GitBranch} label="Şube Sayısı" value={data.branch_count} color="#8B5CF6" delay={240} />
      </div>

      {/* Charts + upcoming */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6 lg:col-span-2 fade-up">
          <h3 className="font-heading font-semibold text-white mb-4">Şube Bazlı Gider Dağılımı</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={branchBar}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip
                contentStyle={{ background: "#111827", border: "1px solid #334155", borderRadius: 12, color: "#fff" }}
                formatter={(v) => fmtTL(v)}
              />
              <Bar dataKey="Maaş" fill="#3B82F6" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Faturalar" fill="#F59E0B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-6 fade-up">
          <h3 className="font-heading font-semibold text-white mb-4">Gider Dağılımı (Fatura + Masraf)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                {pieData.map((e, i) => (
                  <Cell key={i} fill={e.color} stroke="none" />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #334155", borderRadius: 12, color: "#fff" }} formatter={(v) => fmtTL(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1.5">
            {pieData.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} /> {p.name}
                </span>
                <span className="text-white tabular">{fmtTL(p.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming payments */}
      <div className="glass rounded-2xl p-6 fade-up" data-testid="upcoming-payments">
        <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-400" /> Ödeme Günü Yaklaşanlar
        </h3>
        <div className="space-y-2">
          {data.upcoming_payments.slice(0, 6).map((p) => {
            const cat = CATEGORIES[p.category] || CATEGORIES.diger;
            const overdue = p.days_left < 0;
            return (
              <div key={p.id} className="flex items-center justify-between rounded-xl bg-slate-800/40 border border-slate-700/50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: cat.color }} />
                  <div>
                    <div className="text-sm font-medium text-white">{p.title}</div>
                    <div className="text-xs text-slate-400">{cat.label} · {p.due_date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-sm text-white">{fmtTL(p.amount)}</span>
                  <span className={`text-xs font-medium flex items-center gap-1 ${overdue ? "text-rose-400" : p.days_left === 0 ? "text-amber-400" : "text-slate-400"}`}>
                    {overdue && <AlertTriangle className="w-3.5 h-3.5" />}
                    {overdue ? `${Math.abs(p.days_left)}g gecikmiş` : p.days_left === 0 ? "Bugün" : `${p.days_left} gün`}
                  </span>
                </div>
              </div>
            );
          })}
          {data.upcoming_payments.length === 0 && <div className="text-slate-500 text-sm py-4 text-center">Yaklaşan ödeme yok.</div>}
        </div>
      </div>
    </div>
  );
}
